import { clearModal } from '@hixme/modal'
import moment from 'moment'
import { connect } from 'react-redux'
import { change, getFormValues, reduxForm, touch } from 'redux-form'

import notification from 'modules/app-core/notification-manager'
import { getRequestByName } from 'modules/redux-gateway'
import CurrentEmployeeProvider from 'routes/Employees/providers/CurrentEmployeeProvider'
import { actions as personActions } from 'store/modules/persons'

import withFeatureFlags from 'hooks/withFeatureFlags'
import { openActivityLogPopup } from 'modules/activity-log-service/activityLogPopupSlice'
import { ActivityLogAction } from 'modules/activity-log-service/constants'
import { postActivityLog } from 'modules/activity-log-service/helpers'
import { ActivityReasonCategory } from 'types/activity'
import { StopCoverage } from '../components'
import { actions, selectors } from '../modules/cobra-coverage'
import { PUT_COBRA_BENEFITS } from '../modules/cobra-coverage/constants'

const validate = ({
  TerminationDate,
  CobraStatus,
  EmploymentStatus,
  PreviousCoverageEndDate,
  CobraBenefitsEligibleStartDate,
  CobraBenefitsEligibleEndDate,
  CobraBenefitsEffectiveDate,
  CobraContinueBenefits,
}) => {
  const errors = {}

  if (
    !TerminationDate ||
    !moment(TerminationDate, 'MM/DD/YYYY', true).isValid()
  ) {
    errors.TerminationDate = 'Valid Termination Date is required'
  }

  if (!CobraStatus) {
    errors.CobraStatus = 'Cobra Status is required'
  }

  if (!EmploymentStatus) {
    errors.EmploymentStatus = 'Employment Status is required'
  }

  if (!TerminationDate && !PreviousCoverageEndDate) {
    errors.TerminationDate = 'Valid Termination Date is required'
  }

  if (!TerminationDate && !CobraBenefitsEligibleStartDate) {
    errors.TerminationDate = 'Valid Termination Date is required'
  }

  if (!TerminationDate && !CobraBenefitsEligibleEndDate) {
    errors.TerminationDate = 'Valid Termination Date is required'
  }

  if (!TerminationDate && !CobraBenefitsEffectiveDate) {
    errors.TerminationDate = 'Valid Termination Date is required'
  }

  if (CobraStatus === 'Elected' && !CobraContinueBenefits) {
    errors.CobraContinueBenefits = 'At least 1 benefit is required'
  }

  return errors
}

const clearDates = (dispatch) => {
  const autoFillList = [
    'PreviousCoverageEndDate',
    'CobraBenefitsEligibleStartDate',
    'CobraBenefitsEligibleEndDate',
    'CobraBenefitsEffectiveDate',
    'CobraContinueBenefits',
  ]

  autoFillList.forEach((item) => {
    dispatch(change('StopCoverage', item, ''))
  })
}

const formatDisplayDates = (date) => {
  const splitDate = date.split('-')

  return `${splitDate[1]}/${splitDate[2]}/${splitDate[0]}`
}

const convertDates = (person) => {
  const keys = Object.keys(person)
  const personDup = Object.assign({}, person)

  keys.forEach((key) => {
    if (key.includes('Date')) {
      Object.assign(personDup, {
        [key]: formatDisplayDates(person[key]),
      })
    }
  })

  return personDup
}

const mapStateToProps = (state, props) => ({
  formValues: getFormValues('StopCoverage')(state) || {},
  initialValues: {
    ...convertDates(props.person),
  },
  person: props.person,
  benefits: selectors.getBenefits(state),
  isProcessing: getRequestByName(state, PUT_COBRA_BENEFITS).isProcessing,
})

const getEffectiveDates = (dispatch, value) => {
  dispatch(actions.getEffectiveDates(value)).catch((err) => {
    if (err) {
      dispatch(
        notification.actions.createNotification({
          type: 'error',
          message: 'Sorry, we could not retrieve your COBRA dates.',
        })
      )
    }
  })
}

const mapDispatchToProps = (dispatch) => ({
  onEmployeeStateChange: () => {
    dispatch(change('StopCoverage', 'CobraStatus', 'Pending'))
  },
  getEffectiveDates: (e) => {
    const target = e.currentTarget
    const name = target.name
    const value = target.value.replace(/_/g, '')

    dispatch(touch('StopCoverage', name))
    dispatch(change('StopCoverage', name, value))

    if (name === 'TerminationDate') {
      getEffectiveDates(dispatch, value)
    }

    if (value.length === 9 || moment(value, 'MM/DD/YYYY', true).isValid()) {
      clearDates(dispatch)
      getEffectiveDates(dispatch, value)
    }
  },
  resetCobraInfo: (e, value) => {
    clearDates(dispatch)
    dispatch(change('StopCoverage', 'CobraStatus', value))
    getEffectiveDates(dispatch, value)
  },
  editDate: (e) => {
    const target = e.currentTarget
    const parentElement = target.parentElement.parentElement
    const input = parentElement.children[1]

    target.style.zIndex = -1
    input.focus()
  },
})

export default withFeatureFlags(
  CurrentEmployeeProvider(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(
      reduxForm({
        form: 'StopCoverage',
        validate,
        touchOnChange: true,
        onSubmit: (values, dispatch, props) => {
          const { flags } = props
          const onSubmitData = async (formData) => {
            const { reason, reasonCategory, zendeskTicket } = formData

            dispatch(actions.postToPersonsTable(values, props.person))
              .then((updatedPerson) => {
                dispatch(personActions.receivePerson(updatedPerson.person))
                postActivityLog({
                  action: ActivityLogAction.EDIT_COBRA_COVERAGE,
                  description: `edit COBRA coverage for ${values?.FirstName} ${values?.LastName}`,
                  targetUserId: props?.person?.Id ?? props?.person?.id,
                  additionalData: {
                    values,
                    reason,
                    reasonCategory,
                    zendeskTicket,
                  },
                })
                dispatch(clearModal())
              })
              .catch(() => {
                dispatch(
                  notification.actions.createNotification({
                    type: 'error',
                    message: 'Sorry, we could not save your information.',
                  })
                )
              })
          }

          if (
            flags['admin.activity-log-reason-popup-enabled'] &&
            !(props.person.isTestUser ?? props.person.IsTestUser)
          ) {
            dispatch(
              openActivityLogPopup({
                contentText:
                  'Are you sure you want to edit COBRA coverage for this person?',
                onSubmit: onSubmitData,
                required: true,
              })
            )
          } else {
            onSubmitData({
              reason: '',
              reasonCategory: ActivityReasonCategory.OTHER,
              zendeskTicket: '',
            })
          }
        },
      })(StopCoverage)
    )
  )
)
